import { bookActions, favoriteActions } from '@/actions';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { SCREEN_NAME } from '@/constants/analytics';
import { isEmpty, isPlaceFavorite, promiseWrapper, setBookingStartingPoint } from '@/helpers';
import { clearGroupBookingParticipantsSession } from '@/helpers/checkout';
import {
  bookingFlowTrackingSession,
  paymentActivationFlowTrackingSession,
} from '@/helpers/checkoutFlowTrackingSession';
import {
  getBookingEmployee,
  getBookingTrackingProps,
  sendBookingConfirmationSuccessEvent,
} from '@/helpers/confirmation';
import { useAppSelector } from '@/hooks';
import useWindowOnUnload from '@/hooks/useWindowOnUnload';
import { _s } from '@/locale';
import { bookServices } from '@/services/bookServicesTs';
import {
  bookingByCodeResponseSchema,
  confirmCofResponseSchema,
  ConfirmedBooking,
  confirmKlarnaResponseSchema,
  confirmQliroResponseSchema,
  confirmSwishPaymentResponseSchema,
  RetryResponse,
  retryResponseSchema,
} from '@/types/api/services/booking';
import { SelectedPaymentMethod } from '@/types/checkout';
import * as Sentry from '@sentry/react';
import { Dispatch, useEffect, useReducer } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';

const baseTranslationKey = 'pages.booking.confirmation.BookingConfirmation';

const activatePaymentParamSchema = z.object({
  type: z.union([
    z.literal('klarnaActivatePayment'),
    z.literal('qliroActivatePayment'),
    z.literal('cofActivatePayment'),
    z.literal('swishActivatePayment'),
    z.literal('giftcardActivatePayment'),
  ]),
});

const klarnaParamSchema = z.object({
  klarna_order_id: z.string(),
  session_id: z.string(),
  type: z.union([z.literal('klarna'), z.literal('klarnaActivatePayment')]),
  bookingCode: z.string().optional(),
  bookingId: z.string().optional(),
});

const qliroParamSchema = z.object({
  paymentRef: z.string(),
  type: z.literal('qliroActivatePayment').optional(),
});

const cofParamSchema = z.object({
  bookingId: z.string(),
  type: z.union([z.literal('cof'), z.literal('cofActivatePayment')]),
});

const payAtPlaceParamSchema = z.object({
  b: z.string(),
  type: z.literal('giftcardActivatePayment').optional(),
});

const swishParamSchema = z.object({
  bookingId: z.string(),
  type: z.union([z.literal('swish'), z.literal('swishActivatePayment')]),
});

const adyenParamSchema = z.object({
  bookingId: z.string(),
  type: z.literal('a'),
});

const bokadirektPayParamSchema = z.object({
  bookingId: z.string(),
  type: z.literal('bokadirektPay'),
});

const searchParamsSchema = z.union([
  klarnaParamSchema,
  qliroParamSchema,
  cofParamSchema,
  payAtPlaceParamSchema,
  swishParamSchema,
  adyenParamSchema,
  bokadirektPayParamSchema,
]);

type SearchParams = z.infer<typeof searchParamsSchema>;

type CheckoutState = typeof initialState;

type Redirect = RetryResponse['redirect'];

type CheckoutAction =
  | { type: 'SET_BOOKING'; data: { booking: ConfirmedBooking; isActivatePaymentFlow?: boolean } }
  | { type: 'SET_LOADING'; loading: boolean }
  | { type: 'SET_REDIRECT'; redirect: Redirect }
  | { type: 'SET_ERROR' };

const initialState: {
  booking: ConfirmedBooking | null;
  loading: boolean;
  error: boolean;
  redirect?: Redirect;
  selectedPaymentMethod?: SelectedPaymentMethod;
  isActivatePaymentFlow: boolean;
} = {
  booking: null,
  loading: true,
  error: false,
  selectedPaymentMethod: undefined,
  isActivatePaymentFlow: false,
};

async function handleConfirmationError(
  dispatch: Dispatch<CheckoutAction>,
  error: unknown,
  isActivatePaymentFlow: boolean,
) {
  const checkRetry = retryResponseSchema.safeParse(error);

  Sentry.captureException(error);

  if (checkRetry.success) {
    dispatch({ type: 'SET_REDIRECT', redirect: checkRetry.data.redirect });
    return;
  }

  const session = isActivatePaymentFlow ? paymentActivationFlowTrackingSession : bookingFlowTrackingSession;
  session.error(['other']);
  session.cancelSession();

  dispatch({ type: 'SET_ERROR' });
}

async function getAndSetBookingDetails(
  b: SearchParams,
  isActivatePaymentFlow: boolean,
  dispatch: Dispatch<CheckoutAction>,
) {
  clearGroupBookingParticipantsSession();

  if (klarnaParamSchema.safeParse(b).success) {
    const { klarna_order_id, session_id, bookingId } = klarnaParamSchema.parse(b);

    const serviceRequest = isActivatePaymentFlow
      ? () => bookServices.completeKlarnaAfterBooking(klarna_order_id, session_id, +bookingId)
      : () => bookServices.confirmKlarna(klarna_order_id, session_id);

    const { data, error } = await promiseWrapper(serviceRequest());

    const response = confirmKlarnaResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({ type: 'SET_BOOKING', data: { booking: response.data.booking, isActivatePaymentFlow } });
  }

  if (qliroParamSchema.safeParse(b).success) {
    if (isActivatePaymentFlow) {
      paymentActivationFlowTrackingSession.checkoutPaymentMethod('success', 'qliro');
    } else {
      bookingFlowTrackingSession.checkoutPaymentMethod('success', 'qliro');
    }

    const { paymentRef } = qliroParamSchema.parse(b);

    const { data, error } = await promiseWrapper(bookServices.confirmQliro(paymentRef));

    const response = confirmQliroResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({
      type: 'SET_BOOKING',
      data: { booking: response.data.booking, isActivatePaymentFlow },
    });
  }

  if (cofParamSchema.safeParse(b).success) {
    const { bookingId } = cofParamSchema.parse(b);

    const { data, error } = await promiseWrapper(bookServices.confirmCof(bookingId));

    const response = confirmCofResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({
      type: 'SET_BOOKING',
      data: { booking: response.data.booking, isActivatePaymentFlow },
    });
  }

  const adyenOrBokadirektPayParams = z.union([adyenParamSchema, bokadirektPayParamSchema]).safeParse(b);
  if (adyenOrBokadirektPayParams.success) {
    const { bookingId } = adyenOrBokadirektPayParams.data;

    const { data, error } = await promiseWrapper(bookServices.confirmCof(bookingId));

    const response = confirmCofResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({
      type: 'SET_BOOKING',
      data: { booking: response.data.booking, isActivatePaymentFlow },
    });
  }

  if (payAtPlaceParamSchema.safeParse(b).success) {
    const { b: bookingCode } = payAtPlaceParamSchema.parse(b);

    const { data, error } = await promiseWrapper(bookServices.getBookingByCode(bookingCode));

    const response = bookingByCodeResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({ type: 'SET_BOOKING', data: { booking: response.data.booking, isActivatePaymentFlow } });
  }

  if (swishParamSchema.safeParse(b).success) {
    const { bookingId } = swishParamSchema.parse(b);

    const { data, error } = await promiseWrapper(bookServices.confirmSwishPayment(bookingId));

    const response = confirmSwishPaymentResponseSchema.safeParse(data);

    if (!response.success) {
      handleConfirmationError(dispatch, error ?? data, isActivatePaymentFlow);
      return;
    }

    dispatch({ type: 'SET_BOOKING', data: { booking: response.data.booking, isActivatePaymentFlow } });
  }
}

function updateClientPlaceBookings(placeId: number) {
  const clientPlaceBookings = z.array(z.number()).safeParse(localStorage.getItem('mpClientPlaceBookings'));
  if (!clientPlaceBookings.success) return;
  clientPlaceBookings.data.push(placeId);
  localStorage.setItem('mpClientPlaceBookings', JSON.stringify(clientPlaceBookings.data));
}

/**
 * make sure the clearBook function is not called on component unmount when booking again
 * since this will mess up the sessionStorage actions
 */
let bookSameServiceAgain = false;

function clearBookState(state: CheckoutState, appDispatch: Dispatch<any>) {
  // we keep bookState if qliro or klarna payment fails due to time no longer being available
  if (state.redirect?.state?.retry || bookSameServiceAgain) {
    bookSameServiceAgain = false;
    return;
  }
  appDispatch(bookActions.clearBook());
  bookActions.clearTrackingProps();

  bookingFlowTrackingSession.clearTrackingSession();
  paymentActivationFlowTrackingSession.clearTrackingSession();
}

function handleAddPlaceToFavorites(user: any, placeId: number, companyName: string, dispatch: Dispatch<any>) {
  if (!user) return;
  const isFavorite = isPlaceFavorite(user, placeId);

  const showToast = () =>
    toast(({ closeToast }) => (
      <Snackbar
        action={<button onClick={closeToast}>OK</button>}
        label={
          isFavorite
            ? _s(`${baseTranslationKey}.snackbar.removedFromFavorites`)
            : _s(`${baseTranslationKey}.snackbar.addedToFavorites`)
        }
      />
    ));

  const tracking = { companyName, initiatedFrom: SCREEN_NAME.CHECKOUT_BOOKING_CONFIRMATION };
  dispatch(
    isFavorite
      ? favoriteActions.remove(placeId, user, tracking, showToast)
      : favoriteActions.add(placeId, user, tracking, showToast),
  );
}

function reducer(state: CheckoutState, action: CheckoutAction): CheckoutState {
  switch (action.type) {
    case 'SET_BOOKING': {
      const booking = action.data.booking;
      const isActivatePaymentFlow = Boolean(action.data.isActivatePaymentFlow);

      if (!isActivatePaymentFlow) {
        updateClientPlaceBookings(booking.place.id);
      }

      return {
        ...state,
        booking: booking,
        loading: false,
        error: false,
        isActivatePaymentFlow,
      };
    }
    case 'SET_LOADING':
      return { ...state, loading: action.loading, error: false };
    case 'SET_ERROR':
      return { ...state, error: true, loading: false };
    case 'SET_REDIRECT':
      return { ...state, redirect: action.redirect, loading: false };
    default:
      return state;
  }
}

type NextStepsAction = {
  onAddPlaceToFavorites: () => void;
  onBookSameService: () => void;
};

const useConfirmation = (): {
  booking: ConfirmedBooking | null;
  error: boolean;
  redirect?: Redirect;
  loading: boolean;
  selectedPaymentMethod?: SelectedPaymentMethod;
  isActivatePaymentFlow: boolean;
  nextStepsAction: () => NextStepsAction;
} => {
  const location = useLocation<{ selectedPaymentMethod?: SelectedPaymentMethod; isActivatePaymentFlow?: boolean }>();
  const selectedPaymentMethod = location.state?.selectedPaymentMethod;
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    selectedPaymentMethod,
    isActivatePaymentFlow: Boolean(location.state?.isActivatePaymentFlow),
  });
  const history = useHistory();
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const validatedSearchParams = searchParamsSchema.safeParse(searchParams);
  const user = useAppSelector((state) => state.users)?.user;
  const appDispatch = useDispatch();
  const isActivatePaymentFlow =
    (validatedSearchParams.success && activatePaymentParamSchema.safeParse(validatedSearchParams.data).success) ||
    state.isActivatePaymentFlow;

  useEffect(() => {
    if (!state?.booking) return;

    const sendBookingTrackingEvents = async () => {
      const bookingTrackingProps = await getBookingTrackingProps(state.booking);
      if (!bookingTrackingProps) return;
      sendBookingConfirmationSuccessEvent(bookingTrackingProps);
      bookingFlowTrackingSession.checkoutConfirmation({ type: 'booking', data: state.booking });
    };

    const sendActivatePaymentTrackingEvents = async () => {
      const paymentTrackingProps = bookActions.getTrackingProps();
      bookActions.clearTrackingProps();

      if (isEmpty(paymentTrackingProps)) {
        return;
      }

      paymentActivationFlowTrackingSession.checkoutConfirmation({ type: 'booking', data: state.booking });
    };

    if (state?.booking?.bookingCode) {
      if (validatedSearchParams.success === false) {
        history.replace({
          pathname: `/booking/confirmation`,
          search: `?b=${state?.booking?.bookingCode}`,
          state: {
            selectedPaymentMethod,
          },
        });
        sendBookingTrackingEvents();
        return;
      }

      if (isActivatePaymentFlow) {
        sendActivatePaymentTrackingEvents();
      } else {
        sendBookingTrackingEvents();
      }

      history.replace({
        pathname: `/booking/confirmation`,
        search: `?b=${state?.booking?.bookingCode}`,
        state: { selectedPaymentMethod, isActivatePaymentFlow },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.booking]);

  useEffect(() => {
    if (validatedSearchParams.success === false) {
      handleConfirmationError(dispatch, validatedSearchParams.error, isActivatePaymentFlow);
      return;
    }

    getAndSetBookingDetails(validatedSearchParams.data, isActivatePaymentFlow, dispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useWindowOnUnload({ fn: () => clearBookState(state, appDispatch), callOnCleanup: true });

  const nextStepsAction = () => {
    const services =
      state.booking.services
        .map((appointment) =>
          appointment && appointment.service && appointment.service.id ? appointment.service : null,
        )
        .filter((s) => s) || [];

    const employee = getBookingEmployee(state.booking, true);
    const place = state.booking?.place;

    return {
      onAddPlaceToFavorites: () => handleAddPlaceToFavorites(user, place?.id, place?.about?.name, appDispatch),
      onBookSameService: () => {
        bookSameServiceAgain = true;
        bookActions.clearTrackingProps();
        bookingFlowTrackingSession.clearTrackingSession();
        setBookingStartingPoint('book_again_booking_completed');

        const ids = services.map((service) => service.id);
        const filteredServices = services.filter(({ id }, index) => !ids.includes(id, index + 1));

        // @ts-ignore
        appDispatch(bookActions.bookAgain(place, filteredServices, employee));

        const pathname = `/boka-tjanst/${place.about.slug ? place.about.slug + '-' : ''}${place.id}/${
          services[0].about.slug ? services[0].about.slug + '-' : ''
        }${services[0].id}`;

        history.push({ pathname });
      },
    };
  };

  return {
    error: state.error,
    redirect: state.redirect,
    booking: state.booking,
    loading: state.loading,
    selectedPaymentMethod: state.selectedPaymentMethod,
    isActivatePaymentFlow: state.isActivatePaymentFlow,
    nextStepsAction,
  };
};

export default useConfirmation;
